/**
 * The keys this console answers to, written down.
 *
 * Every one of them already worked. F6 leaves a pane's keyboard trap, the arrow
 * keys resize a tile, Space starts a keyboard drag, holding Shift forces a
 * selection over an application's own mouse tracking — and none of that was
 * written anywhere a person would find it. A capability nobody was told about is
 * a capability that does not exist, and the console had accumulated seven of them.
 *
 * An overlay rather than a route, because this is a reference rather than a
 * destination: it is read beside the thing it describes and dismissed. Reached
 * from a permanent control in the top bar, from the command palette, and from
 * F1 — which is what F1 has meant everywhere for thirty years, and was the one
 * key this console was spending on something else. Still **not** `?`: a bare
 * question mark inside a pane belongs to the shell, which is the same rule that
 * keeps both of these on the function row rather than on Ctrl+P.
 *
 * The list lives here rather than in the markup so the palette can offer the same
 * entries without a second copy of them going stale.
 */

import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'

import { Icon } from '@/components/icon'
import { Button } from '@/components/primitives'
import { useT } from '@/i18n/context'
import type { MessageKey } from '@/i18n'

export interface Shortcut {
  /** What is pressed, exactly as it appears on a keyboard. */
  keys: string
  /** What it does, as an i18n key. */
  what: MessageKey
  /** Where it applies, so a key that only works inside a pane says so. */
  scope: MessageKey
}

export interface ShortcutGroup {
  title: MessageKey
  items: Shortcut[]
}

/**
 * Grouped by where an operator is standing when they need them, not by what they
 * do: the question being asked is "what can I press from here".
 *
 * Two rules about the `keys` strings, both learned from this list being wrong.
 *
 * **The standard key is named first, the expert alias second.** Copy mode has
 * always accepted the arrows, Home, End, PageUp and PageDown alongside its vim
 * keys — every one of them was already in `copymode.ts` — and this list printed
 * only "h j k l" and "w b g G". So an operator who does not know vim read the
 * reference and concluded the feature was not for them, while the keys they
 * already knew worked the whole time. A reference that hides the ordinary way in
 * is worse than no reference, because it is believed.
 *
 * **Everything that works is listed.** Eight keys were live and undocumented:
 * the palette's own navigation, Enter and Shift+Enter in both find bars, Ctrl+U
 * and Ctrl+D in copy mode, Enter as a second yank. A capability nobody was told
 * about is a capability that does not exist, which is the sentence this whole
 * overlay was written for — and it applied to the overlay itself.
 */
export const SHORTCUTS: ShortcutGroup[] = [
  {
    title: 'keys.groupConsole',
    items: [
      // F1 is Help on every desktop that has ever had the key. This console
      // spent it on the palette and left the reference with no key at all;
      // the two swapped rather than one of them going without.
      { keys: 'F1', what: 'keys.help', scope: 'keys.scopeAnywhere' },
      { keys: 'F2', what: 'keys.palette', scope: 'keys.scopeAnywhere' },
      // F3 beside them, on the row this console's keyboard already lives on.
      // Not a Ctrl chord: a pane owns Ctrl, where Ctrl+S is XOFF.
      { keys: 'F3', what: 'keys.files', scope: 'keys.scopeAnywhere' },
      { keys: 'Esc', what: 'keys.dismiss', scope: 'keys.scopeOverlay' },
    ],
  },
  {
    title: 'keys.groupPalette',
    items: [
      { keys: '↑ ↓', what: 'keys.paletteMove', scope: 'keys.scopePalette' },
      { keys: 'Enter', what: 'keys.paletteRun', scope: 'keys.scopePalette' },
      { keys: 'Esc', what: 'keys.dismiss', scope: 'keys.scopePalette' },
    ],
  },
  {
    title: 'keys.groupPane',
    items: [
      // The one that most needed writing down: without it a keyboard user is
      // trapped in the terminal, because xterm cancels Tab.
      { keys: 'F6', what: 'keys.leavePane', scope: 'keys.scopePane' },
      { keys: 'Shift + F6', what: 'keys.leavePaneBack', scope: 'keys.scopePane' },
      { keys: 'Shift', what: 'keys.forceSelect', scope: 'keys.scopePane' },
      // The Cmd plane. It is the only one a terminal has no sequence for, which
      // is what makes these three safe where the equivalent Ctrl chords are not
      // — Ctrl+I is Tab and Ctrl+S is XOFF. Listed beside the F-keys rather than
      // in a group of their own: an operator standing in a pane wants one list
      // of what they can press from here.
      { keys: 'Cmd + I', what: 'keys.control', scope: 'keys.scopePane' },
      { keys: 'Cmd + Scroll', what: 'keys.historyOpen', scope: 'keys.scopePane' },
      { keys: 'Cmd + Esc', what: 'keys.backToLive', scope: 'keys.scopeRecord' },
      { keys: 'Enter', what: 'keys.findNext', scope: 'keys.scopeFind' },
      { keys: 'Shift + Enter', what: 'keys.findPrev', scope: 'keys.scopeFind' },
    ],
  },
  {
    title: 'keys.groupCopy',
    items: [
      { keys: '← → ↑ ↓', what: 'keys.copyMove', scope: 'keys.scopeCopy' },
      { keys: 'Home  End', what: 'keys.copyLineEnds', scope: 'keys.scopeCopy' },
      { keys: 'PgUp  PgDn', what: 'keys.copyPage', scope: 'keys.scopeCopy' },
      { keys: 'Ctrl + U / D', what: 'keys.copyHalfPage', scope: 'keys.scopeCopy' },
      { keys: 'V', what: 'keys.copyAnchorLine', scope: 'keys.scopeCopy' },
      { keys: 'v', what: 'keys.copyAnchorChar', scope: 'keys.scopeCopy' },
      { keys: 'y  Enter', what: 'keys.copyYank', scope: 'keys.scopeCopy' },
      { keys: 'Esc', what: 'keys.copyLeave', scope: 'keys.scopeCopy' },
      // Last, and marked as the alias it is: these do nothing the rows above do
      // not, and printing them first is what made the mode look like it required
      // vim to use.
      { keys: 'h j k l · w b · g G · 0 $', what: 'keys.copyVim', scope: 'keys.scopeCopy' },
    ],
  },
  {
    title: 'keys.groupWall',
    items: [
      // A prefix and a digit, which is what a terminal multiplexer has meant by
      // "go to number three" since screen. The seats are drawn on the headers,
      // so the list and the wall cannot disagree about which pane is which.
      { keys: 'Cmd + S · 1-9', what: 'keys.seat', scope: 'keys.scopeWall' },
      // Answered by the framed pane wherever the keyboard is, which is why its
      // scope is the wall and not the pane: an operator standing in the session
      // list is exactly who wants one bigger. The pane header's maximise button
      // does the same thing, as everything on the Meta plane must — Cmd is the
      // Super key away from macOS, and on macOS itself the window manager is
      // likely to read this chord as Minimise before the page ever sees it.
      { keys: 'Cmd + M', what: 'keys.paneFull', scope: 'keys.scopeWall' },
      { keys: 'Space', what: 'keys.dragStart', scope: 'keys.scopeGrip' },
      { keys: '← → ↑ ↓', what: 'keys.dragMove', scope: 'keys.scopeDragging' },
      { keys: '← → ↑ ↓', what: 'keys.spanResize', scope: 'keys.scopeSpan' },
      { keys: 'Esc', what: 'keys.exitFull', scope: 'keys.scopeFull' },
    ],
  },
]

export function ShortcutsOverlay({ onClose }: { onClose: () => void }) {
  const t = useT()
  const boxRef = useRef<HTMLDivElement>(null)

  // The keyboard goes to the thing that just opened, which for a sheet about the
  // keyboard is the least it can do. Through the DOM rather than a forwarded ref,
  // because the button primitive does not take one and threading it through for
  // this would change a shared component to suit one caller.
  useEffect(() => boxRef.current?.querySelector('button')?.focus(), [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      onClose()
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [onClose])

  // Portalled to the body, and that is not tidiness.
  //
  // The control that opens this lives in the top bar, so the overlay was rendered
  // there too — and `.k-topbar` carries `backdrop-filter: blur(6px)`, which makes
  // it a containing block for fixed descendants. The scrim inherited the header's
  // 72px instead of the viewport, and the sheet came out two pixels tall: present
  // in the DOM, addressable by every test that asks whether it is visible, and
  // unreadable. A modal has no business depending on where its trigger happens to
  // sit.
  return createPortal(
    <div
      className="k-keys__scrim"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div ref={boxRef} className="k-keys" role="dialog" aria-modal="true" aria-label={t('keys.title')}>
        <div className="k-keys__head">
          <h2 className="k-keys__title">{t('keys.title')}</h2>
          <Button size="sm" variant="ghost" onClick={onClose} aria-label={t('action.close')}>
            <Icon icon={X} size={15} />
          </Button>
        </div>
        <p className="k-keys__lede">{t('keys.lede')}</p>
        <div className="k-keys__groups">
          {SHORTCUTS.map((g) => {
            // A scope every row in the group shares is one the heading already
            // states. Printing "in copy mode" nine times under a heading that
            // says Copy mode is ink spent on nothing — and it buries the scopes
            // that do carry information, which are the ones that differ from the
            // rows around them.
            //
            // Kept in the data and dropped only at the render: each entry still
            // knows where it applies, which is what the palette and the tests
            // read, and what makes a row safe to move between groups.
            const uniform = g.items.every((s) => s.scope === g.items[0]!.scope)
            return (
              <section key={g.title} className="k-keys__group">
                <h3 className="k-keys__grouptitle">{t(g.title)}</h3>
                <dl className="k-keys__list">
                  {g.items.map((s) => (
                    <div key={`${s.keys}-${s.what}`} className="k-keys__row">
                      <dt className="k-keys__key k-mono">{s.keys}</dt>
                      <dd className="k-keys__what">
                        {t(s.what)}
                        {!uniform && <span className="k-keys__scope">{t(s.scope)}</span>}
                      </dd>
                    </div>
                  ))}
                </dl>
              </section>
            )
          })}
        </div>
      </div>
    </div>,
    document.body,
  )
}
